/**
 * three-mesh-ui helpers with the ClinSpaceXR theme: panels, text, buttons and text fields.
 */
import * as THREE from 'three';
import ThreeMeshUI, { type Block, type CommonOptions, type Text } from 'three-mesh-ui';
import { BASE_PATH } from '../lib/api.ts';
import type { Interactive, InteractiveSystem } from './Interactive.ts';

export const FONT_JSON = `${BASE_PATH}fonts/Roboto-msdf.json`;
export const FONT_PNG = `${BASE_PATH}fonts/Roboto-msdf.png`;

export const THEME = {
  panel: 0x0f141c,
  panelOpacity: 0.9,
  panelEdge: 0x273242,
  text: 0xf2f6fb,
  muted: 0xa9b6c6,
  teal: 0x1593b8,
  tealBright: 0x3fd6ff,
  navy: 0x1a2230,
  button: 0x1a2230,
  buttonHover: 0x24334a,
  buttonActive: 0x1593b8,
  field: 0x0b0f15,
  danger: 0xc8384f,
};

const fontDefaults: CommonOptions = {
  fontFamily: FONT_JSON,
  fontTexture: FONT_PNG,
  fontSupersampling: true,
};

export function panel(options: CommonOptions): Block {
  return new ThreeMeshUI.Block({
    ...fontDefaults,
    backgroundColor: new THREE.Color(THEME.panel),
    backgroundOpacity: THEME.panelOpacity,
    borderRadius: 0.03,
    padding: 0.03,
    contentDirection: 'column',
    justifyContent: 'start',
    alignItems: 'center',
    fontColor: new THREE.Color(THEME.text),
    fontSize: 0.045,
    ...options,
  });
}

export function text(content: string, options: CommonOptions = {}): Text {
  return new ThreeMeshUI.Text({ content, ...options });
}

export function row(options: CommonOptions = {}): Block {
  return new ThreeMeshUI.Block({
    width: options.width ?? 1,
    height: options.height ?? 0.1,
    backgroundOpacity: 0,
    contentDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0.005,
    ...options,
  });
}

export interface ButtonOptions {
  width?: number;
  height?: number;
  fontSize?: number;
  colour?: number;
  hoverColour?: number;
  activeColour?: number;
  textColour?: number;
  margin?: number;
  borderRadius?: number;
}

export interface Button {
  block: Block;
  label: Text;
  interactive: Interactive;
  setLabel(content: string): void;
  setEnabled(enabled: boolean): void;
  setActive(active: boolean): void;
}

export function button(
  system: InteractiveSystem,
  labelText: string,
  onClick: () => void,
  options: ButtonOptions = {},
): Button {
  const colour = new THREE.Color(options.colour ?? THEME.button);
  const hover = new THREE.Color(options.hoverColour ?? THEME.buttonHover);
  const active = new THREE.Color(options.activeColour ?? THEME.buttonActive);
  const block = new ThreeMeshUI.Block({
    width: options.width ?? 0.3,
    height: options.height ?? 0.09,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: options.borderRadius ?? 0.02,
    margin: options.margin ?? 0.006,
    backgroundColor: colour,
    backgroundOpacity: 1,
    fontSize: options.fontSize ?? 0.04,
    fontColor: new THREE.Color(options.textColour ?? THEME.text),
  });
  const label = new ThreeMeshUI.Text({ content: labelText });
  block.add(label);
  let isActive = false;
  let enabled = true;
  const idleColour = () => (isActive ? active : colour);
  block.setupState({
    state: 'idle',
    attributes: { backgroundColor: idleColour(), backgroundOpacity: 1 },
  });
  block.setupState({
    state: 'hovered',
    attributes: { backgroundColor: hover, backgroundOpacity: 1 },
  });
  block.setupState({
    state: 'selected',
    attributes: { backgroundColor: active, backgroundOpacity: 1 },
  });
  block.setState('idle');
  const interactive: Interactive = {
    object: block,
    ui: block,
    touchable: true,
    onSelect: () => {
      if (enabled) onClick();
    },
  };
  system.add(interactive);
  const refreshIdle = () => {
    block.setupState({
      state: 'idle',
      attributes: { backgroundColor: idleColour(), backgroundOpacity: enabled ? 1 : 0.4 },
    });
    if (block.currentState === 'idle' || block.currentState === undefined)
      block.set({ backgroundColor: idleColour(), backgroundOpacity: enabled ? 1 : 0.4 });
  };
  return {
    block,
    label,
    interactive,
    setLabel: (content) => label.set({ content }),
    setEnabled: (value) => {
      enabled = value;
      interactive.enabled = value;
      refreshIdle();
    },
    setActive: (value) => {
      isActive = value;
      refreshIdle();
    },
  };
}

export interface TextField {
  block: Block;
  value: string;
  setValue(value: string): void;
  setFocused(focused: boolean): void;
}

export function textField(
  system: InteractiveSystem,
  placeholder: string,
  onFocus: () => void,
  options: { width?: number; height?: number; fontSize?: number } = {},
): TextField {
  const block = new ThreeMeshUI.Block({
    width: options.width ?? 0.9,
    height: options.height ?? 0.1,
    justifyContent: 'center',
    alignItems: 'start',
    padding: 0.02,
    borderRadius: 0.015,
    backgroundColor: new THREE.Color(THEME.field),
    backgroundOpacity: 1,
    borderWidth: 0.004,
    borderColor: new THREE.Color(THEME.panelEdge),
    fontSize: options.fontSize ?? 0.05,
    fontColor: new THREE.Color(THEME.muted),
  });
  const label = new ThreeMeshUI.Text({ content: placeholder });
  block.add(label);
  block.setupState({
    state: 'idle',
    attributes: { borderColor: new THREE.Color(THEME.panelEdge) },
  });
  block.setupState({
    state: 'hovered',
    attributes: { borderColor: new THREE.Color(THEME.tealBright) },
  });
  block.setupState({
    state: 'selected',
    attributes: { borderColor: new THREE.Color(THEME.tealBright) },
  });
  block.setState('idle');
  system.add({ object: block, ui: block, touchable: true, onSelect: onFocus });
  const field: TextField = {
    block,
    value: '',
    setValue(value: string) {
      field.value = value;
      const showPlaceholder = value.length === 0;
      label.set({
        content: showPlaceholder ? placeholder : value,
        fontColor: new THREE.Color(showPlaceholder ? THEME.muted : THEME.text),
      });
    },
    setFocused(focused: boolean) {
      block.set({
        borderColor: new THREE.Color(focused ? THEME.tealBright : THEME.panelEdge),
        borderWidth: focused ? 0.006 : 0.004,
      });
    },
  };
  return field;
}

/** Keeps a panel facing the viewer (yaw only). */
export function faceCamera(object: THREE.Object3D, camera: THREE.Camera): void {
  const target = camera.getWorldPosition(new THREE.Vector3());
  const pos = object.getWorldPosition(new THREE.Vector3());
  object.rotation.y = Math.atan2(target.x - pos.x, target.z - pos.z);
}

export function updateUi(): void {
  ThreeMeshUI.update();
}
